/**
 * Map zone gradient driven by the SAME per-bin speed spread series as the
 * chart divergence band (binSpeedSpreads). Normalization is relative within
 * the zone — every zone has a reddest spot — and the contrast exponent is
 * monotonic, so the reddest bin is exactly the band's peak bin.
 */
import {
  ensureBinGpsFromTraces,
  type VarianceBin,
} from '../varianceBinProcessing'
import { type SpeedTrace } from './speedTraces'
import {
  SAMPLE_INTERVAL,
  GRADIENT_CONTRAST_EXPONENT,
} from '../../../config/chartDisplay'
import { type ConsistencyDisplayBounds } from './consistencyThresholds'
import { computeZoneSpreadContext } from './binSpeedSpreads'

export interface ZoneVariancePoint {
  lat: number
  lng: number
  distance: number
  normalizedValue: number
}

export interface BuildConsistencyGradientArgs {
  /** Per-lap speed traces on the frame the chart plots (pass 29) — same
   *  series the band consumes, so the reddest bin stays the band's peak. */
  traces: SpeedTrace[]
  excludedLapIds: Set<string>
  displayBounds: ConsistencyDisplayBounds
}

export function buildConsistencyGradient({
  traces,
  excludedLapIds,
  displayBounds,
}: BuildConsistencyGradientArgs): ZoneVariancePoint[] | null {
  const ctx = computeZoneSpreadContext(traces, excludedLapIds, displayBounds)
  if (ctx === null) return null
  const { traces: includedTraces, startDist, numBins, spreads } = ctx

  let peakSpread = 0
  let hasValidBin = false
  for (const s of spreads) {
    if (s !== null) {
      hasValidBin = true
      if (s > peakSpread) peakSpread = s
    }
  }
  if (!hasValidBin) return null

  // GPS per bin via the shared interpolator; `values` is unused here.
  const bins: VarianceBin[] = Array.from({ length: numBins }, (_, i) => ({
    distance: startDist + i * SAMPLE_INTERVAL,
    lat: 0,
    lng: 0,
    values: [],
  }))
  ensureBinGpsFromTraces(bins, includedTraces, startDist)

  const points: ZoneVariancePoint[] = []
  for (let i = 0; i < numBins; i++) {
    const bin = bins[i]
    if (bin.lat === 0 && bin.lng === 0) continue
    const s = spreads[i]
    const normalizedValue =
      s === null || peakSpread <= 0
        ? 0
        : Math.pow(s / peakSpread, GRADIENT_CONTRAST_EXPONENT)
    points.push({
      lat: bin.lat,
      lng: bin.lng,
      distance: bin.distance,
      normalizedValue,
    })
  }
  return points
}
